import Vue from 'vue'
import filesize from 'filesize'

const getDefaultState = () => ({
  biens: [],
  bienCurrent: null,
  arboNot: [],
  questions: null,
  // Distingue « pas encore charge » (questions null, questionsDenied false) de
  // « chargement refuse », que la vue doit afficher differemment.
  questionsDenied: false,
  questionThemes: [],
  documentsDetails: {},
  portefeuilleSettings: null,
  currentPortefeuilleId: null,
  showUnreadOnly: false
})

const state = getDefaultState()

const getters = {

  bien: (state) => (id) => _.find(state.biens, { id }),

  allowQuestions: (state) => state.portefeuilleSettings && state.portefeuilleSettings.allowQuestions,

  allowDownload: (state) => state.portefeuilleSettings && state.portefeuilleSettings.allowDownload,

  filigraneActivated: (state) => state.portefeuilleSettings && state.portefeuilleSettings.filigraneActivated,

  cgaValidated: (state) => state.portefeuilleSettings && state.portefeuilleSettings.cgaValidated,

}

const mutations = {

  LOGOUT (state) {
    Object.assign(state, getDefaultState())
  },

  BIENS (state, payload) {
    state.biens = payload
  },

  BIEN_NOTIFICATIONS (state, payload) {
    const bien = state.biens.find(i => i.id === payload.id)
    if (!bien) return
    bien.notifications = payload.value
  },

  BIEN_CURRENT (state, payload) {
    state.bienCurrent = payload
  },

  PORTEFEUILLE_SETTINGS (state, payload) {
    state.currentPortefeuilleId = +payload
    state.portefeuilleSettings = null
    const bien = state.biens.find(i => i.portefeuille === +payload)
    if (!bien) return
    state.portefeuilleSettings = {
      allowQuestions: bien.allowQuestions,
      allowDownload: bien.allowDownload,
      filigraneActivated: bien.filigraneActivated,
      cgaValidated: bien.cgaValidated
    }
  },

  ARBO_NOT (state, payload) {
    if (state.bienCurrent !== payload.id) return
    state.arboNot = payload.data
  },

  ARBO_NOT_RESET (state) {
    state.arboNot = []
  },

  // `details` et `extension` sont absents de la reponse de l'API, que ARBO_NOT affecte
  // telle quelle a state.arboNot. Vue 2 n'observant que les cles presentes au moment ou il
  // rend un objet reactif, Vue.set est necessaire pour que leur arrivee declenche un rendu.
  //
  // `version` est porte par la reponse de l'API (propertyValue dans lib/mfiles.js) : il est
  // reactif d'origine, une affectation simple suffit.
  ARBO_DETAILS (state, payload) {
    var arbo = state.arboNot.find(item => item.level === payload.payload.arboLevel && item.id === payload.payload.arboID)
    if (!arbo) return
    Vue.set(arbo, 'details', true)
    arbo.documents.forEach((item, i) => {
      const ext = payload.data.find(x => x.id === item.id)
      if (ext === undefined) return
      Vue.set(item, 'extension', ext.extension)
      item.version = ext.version
    })
  },

  COLLECTION_DETAILS (state, payload) {
    var arbo = state.arboNot.find(item => item.level === payload.payload.arboLevel && item.id === payload.payload.arboID)
    if (!arbo) return
    var col = arbo.collections.find(item => item.id === payload.payload.collectionID)
    if (!col) return
    Vue.set(col, 'details', true)
    col.documents.forEach((item, i) => {
      const ext = payload.data.find(x => x.id === item.id)
      if (ext === undefined) return
      Vue.set(item, 'extension', ext.extension)
      item.version = ext.version
    })
  },

  QUESTIONS (state, payload) {
    // Même garde que ARBO_NOT : lors d'une navigation rapide A -> B, la réponse
    // lente de A écrasait les questions de B.
    if (state.currentPortefeuilleId !== +payload.id) return
    state.questions = payload.data
    state.questionsDenied = false
  },

  QUESTIONS_RESET (state, payload) {
    state.questions = null
    state.questionsDenied = false
  },

  QUESTIONS_DENIED (state) {
    state.questions = null
    state.questionsDenied = true
  },

  QUESTION_THEMES (state, payload) {
    state.questionThemes = payload
  },

  DOCUMENT_DETAILS (state, payload) {
    Vue.set(state.documentsDetails, payload.id + '-' + payload.version, payload.data)
  },

  SHOW_UNREAD_ONLY_SET (state, payload) {
    state.showUnreadOnly = payload
  }

}

const actions = {

  async getBiens ({ commit, state }, force = false) {
    if (!force && Object.keys(state.biens).length > 0) return

    const data = await Vue.api.get('/inv/biens')
    commit('BIENS', data)

    if (state.currentPortefeuilleId) {
      commit('PORTEFEUILLE_SETTINGS', state.currentPortefeuilleId)
    }
  },

  async getArboNot ({ commit }, id) {
    const data = await Vue.api.get(`/inv/biens/${id}/arbonotnew`)
    commit('ARBO_NOT', {
      id,
      data: _.orderBy(data, ['level'], ['desc']),
    })
  },

  async getArboDetails ({ commit }, payload) {
    const data = await Vue.api.get(`/inv/details/${payload.arboLevel}/${payload.arboID}`)
    commit('ARBO_DETAILS', { data, payload })
  },

  async getCollectionDetails ({ commit }, payload) {
    const data = await Vue.api.get(`/inv/details/${payload.arboLevel}/${payload.arboID}/${payload.collectionID}`)
    commit('COLLECTION_DETAILS', { data, payload })
  },

  // L'action est declenchee depuis deux endroits (la vue Portefeuille au changement
  // de route, et le composant Questions sur evenement). L'etat de refus est donc
  // porte par le store plutot que par un composant, afin que la vue affiche le bon
  // message quelle que soit l'origine de l'appel.
  async getQuestions ({ commit }, id) {
    try {
      const data = await Vue.api.get(`/inv/questions/${id}`)
      commit('QUESTIONS', { id, data })
    } catch (e) {
      // Un 404 signifie ici « portefeuille hors du perimetre de l'utilisateur » :
      // l'API ne confirme pas l'existence de l'objet.
      if (e.response && e.response.status === 404) {
        commit('QUESTIONS_DENIED')
      }
      throw e
    }
  },

  async getQuestionThemes ({ commit, state }) {
    if (Object.keys(state.questionThemes).length > 0) return

    const data = await Vue.api.get('/inv/questions/themes')
    commit('QUESTION_THEMES', data)
  },

  async getDocumentDetails ({ commit, state }, { id, version }) {
    if (state.documentsDetails[id + '-' + version] !== undefined) return
    const data = await Vue.api.get(`/inv/documents/${id}/${version}/details`)
    data.size = filesize(data.size)
    commit('DOCUMENT_DETAILS', { id, version, data })
  },

  async setBienNotifications ({ commit }, payload) {
    await Vue.api.post(`/inv/biens/${payload.id}/notifications`, {
      value: payload.value,
    })
    commit('BIEN_NOTIFICATIONS', payload)
  },

  async setShowUnreadOnly ({ commit }, payload) {
    commit('SHOW_UNREAD_ONLY_SET', payload)
  }

}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
